import React from 'react';
import { CheckIcon, SectionLabel } from './FeatureRow';
import { descriptionBullets } from '../../utils/trackingLinks';
import { applyCarePlanMode } from './priceMath';
import { CARE_PRICES } from '../../constants/pricing';
import { MONO } from '../../constants/fonts';

function CarePlanPill({ label, mode, percent, custom, original, onClick }) {
  if (!mode || mode === 'none') return null;
  return (
    <button type="button" className="care-plan-pill" onClick={onClick} style={s.carePlanPill}>
      <span style={s.carePlanLabel}>{label}</span>
      <span style={s.carePlanPrice}>
        <span style={s.carePlanStrike}>£{original}</span>
        {mode === 'free' ? 'Free*' : `£${applyCarePlanMode(original, mode, percent, custom)}`}
        {mode !== 'free' && <span style={s.carePlanPerMonth}>/mo</span>}
      </span>
    </button>
  );
}

function CompanyOfferCard({ offer, onCarePlanClick }) {
  const isDiscount = offer.offerType === 'discount';
  const isFree = offer.offerType === 'free';
  const showDiscountCard = isDiscount || isFree;
  const bullets = descriptionBullets(offer.customDescription);
  const categoryLabel = offer.category === 'app' ? 'App' : 'Website';
  const carePrices = CARE_PRICES[offer.category === 'app' ? 'app' : 'website'];
  const essentialMode = offer.essentialPlanMode || 'none';
  const activeMode = offer.activePlanMode || 'none';
  const hasCarePlans = essentialMode !== 'none' || activeMode !== 'none';

  const carePlansBlock = hasCarePlans && (
    <div style={s.carePlansWrap}>
      <p style={s.carePlansLabel}>Care plans, at these rates</p>
      <div style={s.carePlansRow}>
        <CarePlanPill
          label="Essential"
          mode={essentialMode}
          percent={offer.essentialPlanPercent}
          custom={offer.essentialPlanCustom}
          original={carePrices.essential}
          onClick={() => onCarePlanClick?.(offer.category)}
        />
        <CarePlanPill
          label="Active"
          mode={activeMode}
          percent={offer.activePlanPercent}
          custom={offer.activePlanCustom}
          original={carePrices.active}
          onClick={() => onCarePlanClick?.(offer.category)}
        />
      </div>
    </div>
  );

  return (
    <div>
      <style>{`
        @media (max-width: 480px) {
          .offer-card { padding-top: 64px !important; }
        }
      `}</style>
      {showDiscountCard ? (
        <>
          <SectionLabel>Your offer</SectionLabel>
          <h2 style={s.headline}>We'd love to work with {offer.name}.</h2>
          <p style={s.subtitle}>
            We're selective about who we work with, and {offer.name} is exactly the kind of client
            we'd love to have on board.
          </p>
          <div className="offer-card" style={s.card}>
            <span style={s.categoryBadge}>{categoryLabel}</span>
            <p style={s.intro}>
            {isFree
              ? "We'd like to build your site completely free."
              : "We've put together a unique discount for you to consider."}
            </p>
            <p style={s.bigNumber}>{isFree ? 'Free' : `${offer.discountPercent}% off`}</p>
            {bullets.length > 0 && (
              <ul style={s.bulletList}>
                {bullets.map((line, i) => (
                  <li key={i} style={s.bulletItem}>
                    <span style={s.bulletIcon}><CheckIcon /></span>
                    {line}
                  </li>
                ))}
              </ul>
            )}
            {carePlansBlock}
            <p style={s.tagline}>
              Consider it our way of saying: "We really want to build something great with you."
            </p>
            <p style={s.contactLine}>
              Have questions? <a href="/contact" className="contact-btn" style={s.contactLink}>Contact us</a>
            </p>
          </div>
        </>
      ) : (
        <>
          <SectionLabel>Your offer</SectionLabel>
          <h2 style={s.headline}>A quote built for {offer.name}.</h2>
          <p style={s.subtitle}>
            Based on what we've seen of your business, we've put together an initial discounted
            quote for what we think you'll need.
          </p>
          <div className="offer-card" style={s.card}>
            <span style={s.categoryBadge}>{categoryLabel}</span>
            <p style={s.bigNumber}>{offer.customPrice}</p>
            {bullets.length > 0 && (
              <ul style={s.bulletList}>
                {bullets.map((line, i) => (
                  <li key={i} style={s.bulletItem}>
                    <span style={s.bulletIcon}><CheckIcon /></span>
                    {line}
                  </li>
                ))}
              </ul>
            )}
            {carePlansBlock}
            <p style={{ ...s.intro, margin: '24px auto 0' }}>
              Think we've included too much, or missed something? Just let us know. It's completely
              free to chat, ask questions and adjust your quote.
            </p>
            <p style={s.tagline}>
            Consider it our way of saying: "We really want to build something great with you.
            </p>
            <p style={s.contactLine}>
              Have questions? <a href="/contact" className="contact-btn" style={s.contactLink}>Contact us</a>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  headline: {
    fontSize: 'clamp(1.6rem, 5vw, 2.6rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.15,
    color: '#f0f0f5',
    margin: '0 0 16px',
  },
  subtitle: {
    fontSize: '0.92rem',
    color: '#6b7080',
    lineHeight: 1.75,
    maxWidth: 560,
    margin: '0 0 32px',
  },
  card: {
    position: 'relative',
    background: '#16161c',
    border: '1px solid rgba(129,140,248,0.35)',
    borderRadius: 20,
    padding: '40px 32px',
    textAlign: 'center',
  },
  categoryBadge: {
    position: 'absolute',
    top: 20,
    right: 20,
    fontFamily: MONO,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#818cf8',
    background: 'rgba(129,140,248,0.12)',
    padding: '4px 10px',
    borderRadius: 20,
  },
  intro: {
    fontSize: '0.95rem',
    color: '#b0b4cc',
    lineHeight: 1.7,
    maxWidth: 480,
    margin: '0 auto 24px',
  },
  bigNumber: {
    fontFamily: MONO,
    fontSize: 'clamp(2.2rem, 6vw, 3.2rem)',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#818cf8',
    margin: '0 0 16px',
  },
  tagline: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
    color: '#6b7080',
    lineHeight: 1.6,
    maxWidth: 420,
    margin: '24px auto 0',
  },
  contactLine: {
    fontSize: '0.85rem',
    color: '#6b7080',
    margin: '20px 0 0',
  },
  contactLink: {
    color: '#818cf8',
    fontWeight: 600,
    textDecoration: 'none',
  },
  bulletList: {
    listStyle: 'none',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    margin: '0 auto',
    padding: 0,
    maxWidth: 520,
  },
  bulletItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.85rem',
    color: '#b0b4cc',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '8px 16px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
  },
  bulletIcon: {
    color: '#4ade80',
    display: 'flex',
    flexShrink: 0,
  },
  carePlansWrap: {
    margin: '24px 0 0',
  },
  carePlansLabel: {
    fontFamily: MONO,
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#4a4f66',
    margin: '0 0 10px',
  },
  carePlansRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
  },
  carePlanPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(129,140,248,0.08)',
    border: '1px solid rgba(129,140,248,0.25)',
    borderRadius: 20,
    padding: '8px 16px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  },
  carePlanLabel: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#b0b4cc',
  },
  carePlanPrice: {
    fontFamily: MONO,
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#818cf8',
  },
  carePlanStrike: {
    fontWeight: 500,
    color: '#4a4f66',
    textDecoration: 'line-through',
    marginRight: 4,
  },
  carePlanPerMonth: {
    fontWeight: 400,
    color: '#6b7080',
    marginLeft: 2,
  },
};

export default CompanyOfferCard;
